import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import api from 'api';
import type {
  MessagingHandoffProvider,
  MessagingHandoffSecret,
} from 'api/resources/messagingHandoffIntegrations';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { PHONE_NUMBER_MUTATION_KEY } from 'hooks/usePhoneNumber';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import {
  INTEGRATION_LIST,
  type Integration,
  type IntegrationCategory,
  type TelephonyIntegration,
} from '../constants';
import type { IntegrationsResponse } from '../types';
import useIntegrationRecords from './useIntegrationRecords';

// Map our short `{provider}` path segment to the integration tile id used in
// the Integrations constants file. These are the messaging-handoff tiles
// driven by the secret service blob rather than the integration-records table.
const MESSAGING_HANDOFF_TILES: Record<MessagingHandoffProvider, string> = {
  nice: 'nice-messaging-handoff',
  zendesk: 'zendesk-messaging-handoff',
};

/**
 * True when the secret blob has at least one env with populated fields.
 * Empty/undefined secret (including 404) means "not connected".
 */
function isHandoffConnected(
  secret: MessagingHandoffSecret | undefined,
): boolean {
  if (!secret) return false;
  return Object.values(secret).some(
    blob => blob && Object.keys(blob).length > 0,
  );
}

type ActiveIntegrations = Record<IntegrationCategory, IntegrationsResponse[]>;

export const useIntegrations = () => {
  const { accountId, projectId } = useProjectRoute();
  const { hasProjectResourceWriteAccess } = useProjectResource();
  const queryClient = useQueryClient();
  const isPlgProd = useIsPlgProd();
  const { isFeatureOn } = useIsFeatureOn();
  const isIntegrationConfigurationEnabled = isFeatureOn(
    'integrations_configuration',
  );
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const activeTelephonyIntegrations = useQuery({
    queryKey: [accountId, projectId, 'active-telephony-integrations'],
    queryFn: () => api.getActiveTelephonyIntegrations(accountId, projectId),
    enabled:
      !!accountId &&
      !!projectId &&
      !isPlgProd &&
      isIntegrationConfigurationEnabled,
  });

  // Messaging-handoff tiles (NICE, Zendesk) store credentials per-env in the
  // secret service rather than in integration-records. Fetch both to determine
  // which envs are connected and expose them in activeIntegrations.chat so
  // the tiles get the "Connected" badge + per-env labels.
  const messagingHandoffFlagOn = isFeatureOn(
    'messagingHandoffIntegrationsPage',
  );
  const niceHandoffSecret = useQuery({
    queryKey: [accountId, projectId, 'messaging-handoff-secret', 'nice'],
    queryFn: () =>
      api.getMessagingHandoffSecret({
        accountId,
        projectId,
        provider: 'nice',
      }),
    enabled: !!accountId && !!projectId && messagingHandoffFlagOn,
    // Missing secret → 404. Treat as "not connected" rather than an error.
    retry: false,
  });
  const zendeskHandoffSecret = useQuery({
    queryKey: [accountId, projectId, 'messaging-handoff-secret', 'zendesk'],
    queryFn: () =>
      api.getMessagingHandoffSecret({
        accountId,
        projectId,
        provider: 'zendesk',
      }),
    enabled: !!accountId && !!projectId && messagingHandoffFlagOn,
    retry: false,
  });

  const {
    data: activeIntegrationRecords,
    isLoading: isLoadingIntegrationRecords,
  } = useIntegrationRecords();

  const activeIntegrations: ActiveIntegrations = useMemo(() => {
    const base = (activeIntegrationRecords ?? [])?.reduce(
      (acc, integrationRecord) => {
        const provider = integrationRecord.integration;
        const category = INTEGRATION_LIST.find(
          integration => integration.id === provider,
        )?.category;

        if (!category) {
          return acc;
        }

        acc[category].push({
          id: parseInt(integrationRecord.id),
          provider_name: provider,
          account_id: integrationRecord.account_id,
          project_id: integrationRecord.project_id,
          config: integrationRecord.data,
        });
        return acc;
      },
      {
        telephony: activeTelephonyIntegrations.data ?? [],
        chat: [],
        crm: [],
        hospitality: [],
        healthcare: [],
        knowledgeBase: [],
        projectManagement: [],
      } as ActiveIntegrations,
    );

    // Merge messaging-handoff tiles when the secret blob has any populated env.
    // There's no integration-records row for these tiles, so `id` is a synthetic
    // placeholder. Downstream code looks these up by `provider_name` (unique per
    // tile), never by `id` — so the duplicate `-1` values are inert. Do not add
    // consumers that key off `id` without giving each tile a unique value.
    const messagingHandoffSecrets: [
      MessagingHandoffProvider,
      MessagingHandoffSecret | undefined,
    ][] = [
      ['nice', niceHandoffSecret.data],
      ['zendesk', zendeskHandoffSecret.data],
    ];
    for (const [provider, secret] of messagingHandoffSecrets) {
      if (!isHandoffConnected(secret)) continue;
      base.chat.push({
        id: -1,
        provider_name: MESSAGING_HANDOFF_TILES[provider],
        account_id: accountId,
        project_id: projectId,
        config: secret ?? {},
      });
    }

    return base;
  }, [
    activeTelephonyIntegrations.data,
    activeIntegrationRecords,
    niceHandoffSecret.data,
    zendeskHandoffSecret.data,
    accountId,
    projectId,
  ]);

  const disconnectIntegration = async (disconnectEndpoint: string) => {
    if (!accountId || !projectId) {
      throw new Error('Account ID and Project ID are required');
    }

    setIsDisconnecting(true);
    try {
      await api.disconnectTelephonyIntegration({
        accountId,
        projectId,
        disconnectEndpoint,
      });

      // Invalidate and refetch active integrations
      await queryClient.invalidateQueries({
        queryKey: [accountId, projectId, 'active-telephony-integrations'],
      });

      // Invalidate phone numbers
      await queryClient.invalidateQueries({
        queryKey: [accountId, projectId, PHONE_NUMBER_MUTATION_KEY],
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const canConnectIntegration = (integration: Integration) => {
    if (integration.category === 'telephony') {
      // Some telephony integrations only support one handoff method, so we need to reset the handoff configs when connecting them to ensure we don't have any invalid configs.
      // Since this edits handoff configurations, we need to check if the user has write access to the handoffs resource before doing this.
      if (integration.additionalConfig?.shouldResetHandoffConfigs) {
        return (
          hasProjectResourceWriteAccess(PROJECT_RESOURCE.HANDOFFS) &&
          activeIntegrations.telephony.length === 0
        );
      }
      return activeIntegrations.telephony.length === 0;
    }
    return true;
  };

  const activeTelephonyIntegration: TelephonyIntegration | null =
    useMemo(() => {
      const found = INTEGRATION_LIST.find(
        integration =>
          integration.category === 'telephony' &&
          integration.id === activeIntegrations.telephony[0]?.provider_name,
      );
      return (
        found?.category === 'telephony' ? found : null
      ) as TelephonyIntegration | null;
    }, [activeIntegrations.telephony]);

  return {
    isLoading: isLoadingIntegrationRecords,
    activeIntegrations,
    activeTelephonyIntegration,
    disconnectIntegration,
    isDisconnecting,
    canConnectIntegration,
    activeIntegrationRecords,
  };
};
